import { Box, Flex, Grid, GridItem } from '@chakra-ui/react';
import { SecondaryButton } from '../Button';

import ImageShimmer from '../../ImageShimmer/ImageShimmer';

import webinarPolygon from '../../../public/images/resources/webinar-polygon.svg';

export default function Upcoming({
  title,
  acf,
  href,
}: {
  title: string;
  acf: {
    date: string;
    time: string;
    text: string;
    speakers: {
      name: string;
      jobTitle: string;
      image: {
        sourceUrl: string;
      };
    }[];
  };
  href: string;
}) {
  const date = new Date(acf.date).toLocaleString('en-US', {
    // year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
  return (
    <Box
      fontFamily="body"
      m={{ base: '1.5rem 0', md: '3rem 0' }}
      p={{ base: '1rem', md: '2rem 4rem' }}
      bgImage="linear-gradient(0deg, #F6F9FA 0%, rgba(246, 249, 250, 0) 100%)"
      position="relative"
      overflow="hidden"
    >
      <Grid
        templateColumns={{
          md: 'repeat(3, 1fr)',
          lg: '1fr',
          xl: 'repeat(3, 1fr)',
        }}
        columnGap={{ md: '1rem', lg: '0', xl: '3.125rem', '2xl': '0' }}
      >
        <GridItem
          gridArea={{
            md: '1 / 1 / 2 / 3',
            lg: '1 / 1 / 2 / 4',
            xl: '1 / 1 / 2 / 3',
          }}
        >
          <Box
            fontSize="0.75rem"
            lineHeight="1.125rem"
            fontWeight="700"
            color="#909090"
            textTransform="uppercase"
            letterSpacing="0.0625rem"
          >
            Upcoming Webinar
          </Box>

          <Box
            m={{ base: '0.8rem 0', md: '1rem 0' }}
            fontSize={{ base: '1.3rem', md: '1.625rem' }}
            lineHeight={{ base: '1.5rem', md: '1.875rem' }}
            fontWeight="700"
            color="#415F7C"
          >
            {title}
          </Box>

          <Box
            mb="0.4375rem"
            fontSize={{ base: '0.85rem', md: '1.0625rem' }}
            lineHeight={{ base: '0.9rem', md: '1.25rem' }}
            fontWeight="700"
            color="#86B75C"
          >
            {date} • {acf.time} PT
          </Box>

          <Box
            maxW={{ xl: '25rem' }}
            fontSize={{ base: '0.75rem', md: '0.9375rem' }}
            lineHeight={{ base: '0.9rem', md: '1.125rem' }}
            fontWeight="600"
            color="#909090"
          >
            {acf.speakers.map((speaker) => `${speaker.name}, ${speaker.jobTitle}`).join(' and ')}
          </Box>
        </GridItem>
        <GridItem
          gridArea={{
            md: '1 / 3 / 3 / 4',
            lg: '2 / 1 / 3 / 4',
            xl: '1 / 3 / 3 / 4',
          }}
        >
          <Flex
            mt={{ base: '1rem', md: '0', lg: '1rem', xl: '0' }}
            flexWrap={{
              base: acf.speakers.length === 4 ? 'wrap' : 'nowrap',
            }}
            w={{
              md: acf.speakers.length === 4 && '15.625rem',
              xl: acf.speakers.length === 4 && '16.25rem',
            }}
          >
            {acf.speakers.map((speaker, i) => {
              const WebinarSpeakers = (props) => (
                <Box
                  position="relative"
                  borderRadius="50%"
                  overflow="hidden"
                  boxShadow="4px 4px 12px rgba(0, 0, 0, 0.12)"
                  {...props}
                >
                  <ImageShimmer
                    src={speaker.image.sourceUrl}
                    alt={speaker.name}
                    style={{ objectFit: 'cover' }}
                    fill
                  />
                </Box>
              );

              switch (acf.speakers.length) {
                case 1:
                  return (
                    <WebinarSpeakers
                      key={speaker.name}
                      w={{ base: '6.25rem', sm: '8.875rem' }}
                      h={{ base: '6.25rem', sm: '8.875rem' }}
                    />
                  );

                case 2:
                  return (
                    <WebinarSpeakers
                      key={speaker.name}
                      w={{ base: '6.25rem', xl: '8.875rem' }}
                      h={{ base: '6.25rem', xl: '8.875rem' }}
                      mr={{
                        base: i === 0 && '1rem',
                        md: i === 0 && '-1rem',
                        lg: i === 0 && '1rem',
                        xl: i === 0 && '-1rem',
                      }}
                      mt={{
                        md: i === 0 && '4.5rem',
                        lg: i === 0 && '0',
                        xl: i === 0 && '6rem',
                      }}
                      mb={{
                        md: i === 0 && '-4.5rem',
                        lg: i === 0 && '0',
                        xl: i === 0 && '-6rem',
                      }}
                    />
                  );

                case 3:
                  return (
                    <WebinarSpeakers
                      key={speaker.name}
                      w={{ base: '6.25rem', xl: '7rem' }}
                      h={{ base: '6.25rem', xl: '7rem' }}
                      mr={{
                        base: i !== 3 && '1rem',
                        md: i === 0 ? '2rem' : '',
                        lg: i !== 3 && '1rem',
                        xl: i === 0 ? '2rem' : '',
                      }}
                      mt={{
                        md: i === 2 ? '7rem' : '',
                        lg: '0',
                        xl: i === 2 ? '7.5rem' : '',
                      }}
                      mb={{
                        md: i === 0 && '-4.5rem',
                        lg: i === 0 && '0',
                        xl: i === 0 && '-6rem',
                      }}
                      ml={{
                        md: i === 2 ? '-11.125rem' : '',
                        lg: '0',
                        xl: i === 2 ? '-12.5rem' : '',
                      }}
                    />
                  );

                case 4:
                  return (
                    <WebinarSpeakers
                      key={speaker.name}
                      w={{ base: '6.25rem', xl: '7rem' }}
                      h={{ base: '6.25rem', xl: '7rem' }}
                      mr={{
                        base: i !== 3 && '1rem',
                        lg: i !== 3 && '1rem',
                      }}
                      mt={{
                        sm: '0',
                        lg: '0',
                      }}
                      mb={{
                        base: '1rem',
                      }}
                    />
                  );

                default:
                  break;
              }
            })}
          </Flex>
        </GridItem>
        <GridItem
          gridArea={{
            md: '2 / 1 / 3 / 2',
            lg: '3 / 1 / 4 / 4',
            xl: '2 / 1 / 3 / 2',
          }}
        >
          <SecondaryButton mt={{ base: '1.2rem', md: '1.5rem' }} href={href}>
            Register Now
            <Box ml="1rem">
              <svg
                width="9"
                height="14"
                viewBox="0 0 9 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M1 13L7 7L0.999999 1" stroke="white" strokeWidth="2" />
              </svg>
            </Box>
          </SecondaryButton>
        </GridItem>
      </Grid>

      <Box
        position="absolute"
        bottom="0"
        right="0"
        zIndex="-1"
        w={{ base: '22.125rem' }}
        h={{ base: '19.3125rem' }}
        bgImage={`url(${webinarPolygon.src})`}
        bgRepeat="no-repeat"
        bgSize={{ base: 'cover' }}
        bgPosition={{ base: '0 10px' }}
      />
    </Box>
  );
}
